/**
 * use-evotech-order.ts — Tạo đơn Evotech ngay trong khung chat (nút "Tạo đơn") theo luồng POS của Evotech: phiên thanh toán
 * (giá, voucher, trừ điểm, phí ship do Evotech tính) → tạo đơn.
 * Backend: ecdp_backend_source/src/modules/chat/chat-evotech-order-routes.ts (+ integrations/evotech/evotech-checkout.ts)
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'

export type DeliveryType = 'InShop' | 'ExpressDelivery'

export interface EvotechOrderOptions {
  shopName: string | null
  contact: { name: string | null; phone: string | null }
  /** linked = đã liên kết; found = trùng SĐT (liên kết khi tạo đơn); new = tạo khách Evotech khi tạo đơn; blocked = không tạo được. */
  customer:
    | { state: 'linked' | 'found'; userNo: number; name: string | null; points: number | null }
    | { state: 'new' }
    | { state: 'blocked'; reason: string }
  branches: { branchId: string; name: string; address: string | null; isDefault: boolean }[]
  /** allowed = COD / Tiền mặt bật cho bán hàng POS ở Evotech; reason = vì sao không chọn được. */
  payments: { typePay: string; name: string; allowed: boolean; reason: string | null }[]
  addresses: { shippingAddressId: string; fullName: string | null; phoneNumber: string | null; address: string | null; isDefault: boolean }[]
}

export interface NewAddress {
  fullName: string
  phoneNumber: string
  provinceId: string
  wardId: string
  address: string
}

export interface EvotechOrderDraft {
  items: { productId: string; quantity: number }[]
  delivery: DeliveryType
  branchId: string | null
  addressId: string | null
  newAddress: NewAddress | null
  typePay: string
  notes: string | null
  voucherCodes: string[]
  usePoints: boolean
}

export interface CheckoutQuote {
  subtotal: number
  comboDiscount: number
  promotionDiscount: number
  shippingFee: number
  shippingDiscount: number
  pointDiscount: number
  tax: number
  total: number
  /** Số điểm Evotech sẽ trừ khi bật "Dùng điểm" (tối đa được phép). */
  pointsUsable: number
  pointsEarned: number
}

export interface CheckoutState {
  sessionId: string
  customerId: string | null
  usePoints: boolean
  voucherCodes: string[]
  items: { cartItemId: string; itemsId: string; name: string; quantity: number; unitPrice: number | null; originalPrice: number | null; total: number | null; isGift: boolean }[]
  quote: CheckoutQuote
  stockIssues: string[]
}

export interface DraftResult {
  session: CheckoutState
  /** Mã voucher Evotech không áp được (bỏ khỏi lựa chọn). */
  droppedVoucherCodes: string[]
  /** Phiên đã gắn khách Evotech (voucher / điểm dùng được). */
  hasCustomer: boolean
  /** productId eCDP → itemsId Evotech (ghép đơn giá Evotech vào từng dòng). */
  lineItems: Record<string, string>
}

export interface VoucherOption {
  code: string
  voucherId: string
  name: string
  kind: 'Promotion' | 'Transport' | 'Custom'
  value: string
  minOrder: number | null
  endDate: string | null
  collected: boolean
  pointCost: number | null
  image: string | null
}

export interface CreatedEvotechOrder {
  orderNo: string
  orderId: string | null
  total: number
  pointsUsed: number
  confirmation: { sent: boolean; error: string | null } | null
}

const base = (convId: string) => `/conversations/${convId}/evotech-order`

export function useEvotechOrderOptions(convId: string, enabled: boolean) {
  return useQuery<EvotechOrderOptions>({
    queryKey: ['evotech-order', 'options', convId],
    queryFn: async () => (await api.get<EvotechOrderOptions>(`${base(convId)}/options`)).data,
    enabled,
    staleTime: 30_000,
    retry: false,
  })
}

/** Tỉnh (không truyền provinceId) hoặc phường / xã của một tỉnh — đơn vị hành chính mới. */
export function useEvotechPlaces(convId: string, provinceId: string | null, enabled: boolean) {
  return useQuery<{ id: string; name: string }[]>({
    queryKey: ['evotech-order', 'places', provinceId ?? 'provinces'],
    queryFn: async () =>
      (await api.get<{ places: { id: string; name: string }[] }>(`${base(convId)}/places`, { params: provinceId ? { provinceId } : undefined })).data.places,
    enabled,
    staleTime: 24 * 3600_000,
  })
}

/** Phiên đang dùng của popup (giữ qua các lần tính lại); hàng đợi để các lần gọi không chạy song song (lần đầu tạo phiên). */
export interface SessionHolder {
  id: string | null
  chain: Promise<unknown>
}

/**
 * Tính lại đơn trên phiên thanh toán Evotech theo bản nháp (tạo phiên ở lần đầu). `draft` null = chưa đủ dữ liệu.
 * Ghi chú / địa chỉ mới không ảnh hưởng giá → bên gọi bỏ ra khỏi `draft` để không gọi lại khi đang gõ.
 */
export function useEvotechCheckout(convId: string, draft: EvotechOrderDraft | null, holder: SessionHolder) {
  return useQuery<DraftResult>({
    queryKey: ['evotech-order', 'draft', convId, draft],
    queryFn: () => {
      const run = holder.chain.then(async () => {
        const r = (await api.post<DraftResult>(`${base(convId)}/draft`, { ...draft, sessionId: holder.id })).data
        holder.id = r.session.sessionId
        return r
      })
      holder.chain = run.catch(() => undefined)
      return run
    },
    enabled: !!draft && draft.items.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false,
  })
}

export function useEvotechVouchers(convId: string, sessionId: string | null, enabled: boolean) {
  return useQuery<{ vouchers: VoucherOption[]; reason?: string }>({
    queryKey: ['evotech-order', 'vouchers', convId, sessionId],
    queryFn: async () => (await api.get<{ vouchers: VoucherOption[]; reason?: string }>(`${base(convId)}/vouchers`, { params: { sessionId } })).data,
    enabled: enabled && !!sessionId,
    staleTime: 60_000,
    retry: false,
  })
}

export function useCollectVoucher(convId: string, sessionId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (code: string) =>
      (await api.post<{ vouchers: VoucherOption[] }>(`${base(convId)}/vouchers/collect`, { sessionId, code })).data,
    onSuccess: (data) => {
      qc.setQueryData(['evotech-order', 'vouchers', convId, sessionId], data)
      // Voucher đổi điểm trừ điểm của khách → cập nhật số điểm hiện có.
      qc.invalidateQueries({ queryKey: ['evotech-order', 'options', convId] })
    },
  })
}

/** Huỷ phiên khi đóng popup không tạo đơn (phiên treo hiện ở màn POS của cửa hàng). Lỗi bỏ qua. */
export function cancelEvotechCheckout(convId: string, sessionId: string | null): void {
  if (!sessionId) return
  api.delete(`${base(convId)}/session/${sessionId}`).catch(() => undefined)
}

export function useCreateEvotechOrder(convId: string, contactId: string | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: EvotechOrderDraft & { sessionId: string; sendConfirmation: boolean }) =>
      (await api.post<CreatedEvotechOrder>(base(convId), body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['evotech-order', 'options', convId] })
      qc.invalidateQueries({ queryKey: ['conversation-messages', convId] })
      if (contactId) {
        qc.invalidateQueries({ queryKey: ['contact-orders', contactId] })
        qc.invalidateQueries({ queryKey: ['evotech', 'member', contactId] })
      }
    },
  })
}
